import { Calendar, FileText, PackageCheck, Warehouse, X } from 'lucide-react';
import type { BackorderRow, BackorderStatus } from '@eombor/contracts';
import { backorderStatusLabel } from '@eombor/contracts';
import { Badge, Button, Card, cn, type BadgeTone } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { PickBox } from './PickBox.js';
import type { BackorderSelection } from '../model/selection.js';

const TONES: Record<BackorderStatus, BadgeTone> = {
  SUBMITTED: 'info',
  ACCEPTED: 'warn',
  FULFILLED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const dateText = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/**
 * BITTA BILDIRGI — ro'yxatdagi kartochka.
 *
 * Mahsulot nomi eng yirik: odam ro'yxatni «nima so'ralgan» bo'yicha ko'zdan
 * kechiradi, hujjat raqami bo'yicha emas.
 *
 * Erkin matnli, hali biriktirilmagan bildirgi ALOHIDA ko'rinadi (qiya yozuv
 * va belgi): u katalogdagi mahsulot emas, odamning so'zi — skladchi buni
 * bir qarashda ajratishi kerak, chunki uni qabul qilish qo'shimcha qadam
 * talab qiladi.
 */
export const BackorderCard = ({
  row,
  onAccept,
  onFulfil,
  onCancel,
  selection,
}: {
  row: BackorderRow;
  onAccept?: (row: BackorderRow) => void;
  onFulfil?: (row: BackorderRow) => void;
  onCancel?: (row: BackorderRow) => void;
  /** Belgilash — faqat «Kutilmoqda» bo'limida beriladi. */
  selection?: BackorderSelection;
}) => {
  const unbound = row.nomenclatureId === null;

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start gap-2">
        {selection === undefined ? null : (
          <span className="pt-1">
            <PickBox row={row} selection={selection} />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className={cn('text-base font-semibold text-text', unbound && 'italic')}>
            {row.nomenclatureName ?? row.freeText}
            {unbound ? (
              <span className="ml-2 align-middle text-xs font-normal text-warn not-italic">
                {t('backorder.unbound')}
              </span>
            ) : null}
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
            <span className="tabular font-semibold text-text">
              {qtyText(row.requestedQty)} {row.unit ?? ''}
            </span>
            <span className="inline-flex items-center gap-1">
              <FileText size={12} aria-hidden />
              {row.docNo}
            </span>
            <span className="inline-flex items-center gap-1">
              <Warehouse size={12} aria-hidden />
              {row.warehouseName}
            </span>
            {row.neededBy === null ? null : (
              <span className="inline-flex items-center gap-1">
                <Calendar size={12} aria-hidden />
                {dateText(row.neededBy)}
              </span>
            )}
          </p>
        </div>
        <Badge tone={TONES[row.status]}>{backorderStatusLabel(row.status)}</Badge>
      </div>

      <p className="border-l-2 border-accent/40 pl-2 text-sm text-text-muted italic">
        {row.purpose}
      </p>

      <p className="text-xs text-text-muted">
        {row.requesterName} · {dateText(row.createdAt)}
      </p>

      {/* Rad etish sababi kartochkaning O'ZIDA: buyurtmachi uni ko'rish
          uchun hujjatni ochishga majbur bo'lmasligi kerak. */}
      {row.rejectReason === null ? null : (
        <p className="rounded-[var(--radius-sm)] bg-danger-soft px-2.5 py-2 text-sm text-danger">
          {row.rejectReason}
        </p>
      )}

      {row.requisitionDocNo === null ? null : (
        <p className="text-sm text-success">
          {t('backorder.becameRequisition', { docNo: row.requisitionDocNo })}
        </p>
      )}

      {/* Erkin qoldiq faqat «Kutilmoqda» ro'yxatida, bog'langan bildirgida
          to'ldiriladi. Nol ko'rsatilmaydi: «omborda 0 turibdi» hech narsa
          aytmaydi, skladchi esa baribir istalgan mahsulotni bera oladi. */}
      {row.availableQty === null || row.availableQty <= 0 ? null : (
        <p className="tabular text-sm text-success">
          {t('backorder.arrived', { qty: qtyText(row.availableQty), unit: row.unit ?? '' })}
        </p>
      )}

      {onAccept === undefined && onFulfil === undefined && onCancel === undefined ? null : (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
          {onCancel === undefined ? null : (
            <Button variant="ghost" size="sm" onClick={() => onCancel(row)}>
              <X size={14} aria-hidden />
              {t('backorder.withdraw')}
            </Button>
          )}
          {onAccept === undefined ? null : (
            <Button size="sm" onClick={() => onAccept(row)}>
              {t('backorder.decide')}
            </Button>
          )}
          {onFulfil === undefined ? null : (
            <Button size="sm" onClick={() => onFulfil(row)}>
              <PackageCheck size={14} aria-hidden />
              {t('backorder.fulfil')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
